import { useId, useMemo, useState } from "react";
import { m } from "@/paraglide/messages";
import type { CalcConfig } from "../lib/calculators";
import { CalcField } from "./CalcField";
import { FinanceResult } from "./FinanceResult";

/** Config-driven investment calculator. All math comes from the pure compute fn. */
export function FinanceCalc({ config }: { config: CalcConfig }) {
  const uid = useId();
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(config.fields.map((f) => [f.id, f.defaultValue ?? ""])),
  );

  const result = useMemo(() => {
    const nums: Record<string, number> = {};
    for (const f of config.fields) {
      const raw = values[f.id];
      nums[f.id] = raw === "" || raw == null ? Number.NaN : Number(raw);
    }
    return config.compute(nums);
  }, [values, config]);

  const cols =
    config.fields.length >= 4
      ? "sm:grid-cols-2 lg:grid-cols-4"
      : "sm:grid-cols-3";

  return (
    <div className="rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
      <div className={`grid gap-4 ${cols}`}>
        {config.fields.map((f) => (
          <CalcField
            key={f.id}
            domId={`${uid}-${f.id}`}
            label={f.label()}
            suffix={f.suffix}
            value={values[f.id]}
            onChange={(v) => setValues((s) => ({ ...s, [f.id]: v }))}
          />
        ))}
      </div>

      <div className="mt-6">
        {result ? (
          <FinanceResult items={result} />
        ) : (
          <p className="text-sm text-muted-foreground">
            {m.sip_enter_values()}
          </p>
        )}
      </div>
    </div>
  );
}
